import React, {
    forwardRef,
    useState,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import _get from 'lodash/get';
import _noop from 'lodash/noop';

import COLORS from '@mlb/react-patterns/style/universal.color.ts';
import CardPattern  from '@mlb/react-patterns/components/ContentCard/contentCard.tsx';
import {
    ContentWrapper,
    DataWrapper,
    TitleWrapper,
    Metadata,
}  from '@mlb/react-patterns/components/ContentCard/contentCard.style';
import { ImageWrapper, Placeholder } from '@mlb/react-patterns/components/Image/image.style';

import { getMediaPath } from '../../utils';
import { Tracking } from 'bdvp-tracking';

import { targetHasDataClickHandler, DateUtils } from '../../utils';

import DeleteReel from '../../containers/DeleteReelButton';
import ShareButton from '../../components/ShareButton';
import OptionsMenu from './OptionsMenu';
import ToggleInReel from './ReelButton';
import { APP_VIEW } from '../../utils/constants';

export const cardContexts = {
    REEL_PAGE: 'reelPage',
    MEDIA: 'media',
};

const ShareComponent = styled(ShareButton).attrs((props) => ({
    dataAttr:{
        'data-click-handler': true,
    },
    ...props,
}))`
    &&& {
        text-align: left;
        width: auto;
        white-space: nowrap;
    }
`;

const ContentCard = styled(CardPattern)`
    cursor: pointer;

    ${({ disabled }) => disabled && `
        display: block;
        pointer-events: none;
    `}

    ${Placeholder} {
        height: 0;
        padding-bottom: 56.25%;
        background-color: ${COLORS.mlbGrey7};
    }

    ${ContentWrapper} {
        ${({ orientation, isLoading }) => orientation === 'landscape' && !isLoading && 'display: flex;' };
    }

    ${Metadata} {
        flex: 1;
    }

    ${({ orientation }) => orientation === 'landscape' && `
        ${TitleWrapper} {
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            max-height: 72px;
        }
    `}

    ${DataWrapper} {
        width: 100%;
        flex-wrap: wrap;
    }

    ${ImageWrapper} {
        ${({ isInReel }) => isInReel && `
            &:before {
                content: '';
                position: absolute;
                top: 8px;
                right: 10px;
                width: 24px;
                height: 24px;
                background: ${COLORS.liveYellow};
                border-radius: 50%;
                z-index: 1;
            }
            &:after {
                content: '';
                display: block;
                position: absolute;
                top: 14px;
                right: 15px;
                width: 12px;
                height: 6px;
                transform: rotate(-45deg);
                border-left: 2px solid ${COLORS.mlbGrey8};
                border-bottom: 2px solid ${COLORS.mlbGrey8};
                z-index: 1;
            }
        `}
    }
`;

const IMAGE_PLACEHOLDER_SVG = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMDg0IDU3NiIgcHJlc2VydmVBc3BlY3RSYXRpbz0ibm9uZSI+PHBhdGggZmlsbD0iI0VDRUNFQyIgZD0iTTAgMGgxMDg0djU3NkgweiIvPjxnIG9wYWNpdHk9Ii4wOCI+PHBhdGggZD0iTTYyMi42ODQgMjMwLjk3M0ExMC4xOCAxMC4xOCAwIDAwNjE1LjQ4IDIyOGwtMTU2LjMyOC4xNTJhMTAuMTA1IDEwLjEwNSAwIDAwLTcuMTgzIDIuOThjLTEuOTIgMS45Mi0yLjk4NyA0LjQ3NC0yLjk3IDcuMTk2bC4wNiA2OS45MDVhMTAuMTIgMTAuMTIgMCAwMDIuOTkgNy4xODcgMTAuMSAxMC4xIDAgMDA3LjE4MiAyLjk3N2wxNTYuMzQtLjE1MmM1LjYwOCAwIDEwLjE3My00LjU3IDEwLjE3My0xMC4xNzdsLS4wODUtNjkuOTFjMC0yLjcxMi0xLjA2NS01LjI1Ny0yLjk3Ni03LjE4NXoiIGZpbGw9IiNGRkYiLz48cGF0aCBkPSJNNjIyLjY4NCAyMzAuOTczQTEwLjE4IDEwLjE4IDAgMDA2MTUuNDggMjI4bC00My42OTIuMDUgMjUuMTkgNDEuOTMgMi4yNzQuMjg2IDEuMzUzIDEuOTA3LjAxNiAxLjgxNCAxLjYxNy4zMDQgMS40NTQgMS45OTd2MS43MjVsMS42MTcuMjkyczEuNjMgMS43MTMgMS42MyAxLjgwNWwuMDI3IDMuOTIyczMuNTM0IDMuMjYgNi42ODggNC41YzIuMjI0Ljg2IDIuNDk1IDQuMzk2IDMuNzcyIDYuMzE2IDEuNzA4IDIuNjcgNC4wMDggMy44MjYgMy41MzUgNS4zNjUtMS4xMzQgNC4xMS01LjM1NCAxMS4wMTQtOS4yNzcgMTEuMzk4IDAgMC05Ljc4NC4wMjItMTUuNTE3LjAzdjYuNjI1bDE5LjQwNi0uMDJjNS42MDcgMCAxMC4xNzItNC41NyAxMC4xNzItMTAuMTc4bC0uMDg1LTY5LjkxYzAtMi43MS0xLjA2NS01LjI1Ni0yLjk3Ni03LjE4NHoiLz48cGF0aCBkPSJNNTU2Ljg5NyAyMjguMDY4bC05Ny43NDUuMDg0YTEwLjEwNSAxMC4xMDUgMCAwMC03LjE4MyAyLjk4Yy0xLjkyIDEuOTItMi45ODcgNC40NzQtMi45NyA3LjE5NmwuMDYgNjkuOTA1YTEwLjEyIDEwLjEyIDAgMDAyLjk5IDcuMTg3IDEwLjEgMTAuMSAwIDAwNy4xODIgMi45NzdsNjMuNDk1LS4wNjNjLTEuNTkzLTIuNjUtMy4zODctNS42Ny0zLjk0OC02LjU4OGwtNy41NTcuMDEzYy0uMDM0LTE4Ljc3MyA2LjM2My0yOS4yMTYgMTMuOTIzLTMxLjIzNyAxLjA2Mi0uMTk1LjU3Ni01LjM3LS43Ny02LjkgMCAwLTMuNjMuMS00LjM5Ni0uMDktLjk1Ni0uMjktLjMtMS4yMzctLjMtMS4yMzdsMy41NC03LjY3NC0uNDgzLTIuMTEtMTMuMTI4LjAyNmMtLjE4NyAwIDEwLjgxLTcuNTggMTAuODEtNy41OC41NS0xOS45MiAyMC45NDYtMjEuNDc4IDMzLjMyNS0xMy4yNjggNy4yNzIgNC44ODQgNy44NzMgMTQuNDYgNy4yOTggMjEuMTUzLS4wOTQuMzg0LTEuOTEyLjEtMS45MTIuMXMtMS4yNCA3LjI4IDIuMDMgNy4yOGwxNC4zNS0uMDI0YzUuODM1LS4yMDQgMTEuNSAzLjcwNyAxMS41IDMuNzA3bDEuNDItNC45NjgtMzEuNTMzLTQwLjg3MnptLTg2LjgxNiA3NS45MmMtMy44Ni4wMTQtNi45ODQtMy4xMDctNi45OTYtNi45NjdhNi45OTIgNi45OTIgMCAwMTYuOTc2LTYuOTkyYzMuODQ4LS4wMSA2Ljk3NiAzLjExNiA2Ljk4NSA2Ljk3NmE2Ljk2OCA2Ljk2OCAwIDAxLTYuOTY0IDYuOTg1em03MC4xOTIgNDQuMDQ3Yy44NyAwIDEuNjI0LjI5NiAyLjI1OC45MTguNjI2LjYwNC45NDciLz48L2c+PC9zdmc+';

const Card = forwardRef(({
    appView,
    basePath,
    mediaPlayback,
    orientation,
    onClick,
    isLoading,
    config,
    lang,
    isActive,
    dictionary,
    cardContext,
    renderStats,
    searchParams,
    imgSizes,
    preventOverflow,
    popoverPlacement,
}, ref) => {

    const [
        isPopoverOpen,
        setIsPopoverOpen,
    ] = useState(false);

    const onPopoverOpen = useCallback(() => setIsPopoverOpen(true), [setIsPopoverOpen]);
    const onPopoverClose = useCallback(() => setIsPopoverOpen(false), [setIsPopoverOpen]);

    if(isLoading) return (
        <ContentCard
            orientation={orientation}
            isLoading={isLoading}
            headlineWidth={'auto'}
            imageProps={{
                fixAspect: true,
                ratio: '16:9',
                borderRadius: '6px',
            }}
        />
    );

    const blurb = _get(mediaPlayback, 'blurb');
    const date = _get(mediaPlayback, 'date', '');

    const formattedDate = DateUtils.formatDate(date, lang);

    const id = _get(mediaPlayback, 'id', '');
    const slug = _get(mediaPlayback, 'slug', '');
    const isAppView = appView !== APP_VIEW.ballpark;
    const isInReel = isAppView && _get(mediaPlayback, 'isInReel', false);
    const feeds = _get(mediaPlayback, 'feeds', []);
    const hasNoFeeds = feeds.length === 0;
    const imageCuts = _get(feeds, '[0].image.cuts') || [];
    const imageAlt = _get(feeds, '[0]image.altText');
    const imageSrc = _get(imageCuts, '[0].src', '');
    const errorImageSrc = _get(config, 'errorImageSrc', '');
    const imageSrcSet = imageCuts.map((img) => `${img.src} ${img.width}w`).join(', ');
    const playbacks = _get(feeds, '[0].playbacks');
    const canAddToReel = _get(mediaPlayback, 'canAddToReel', true);
    const mediaPath = getMediaPath(mediaPlayback);

    const disabled = hasNoFeeds || !slug || (playbacks && playbacks.length === 0);
    let duration = _get(feeds, '[0].duration', '');
    const durationDisabledLabel = 'Not Available';

    if (disabled) {

        duration = durationDisabledLabel;

    }

    const imageProps = {
        src: imageSrc || IMAGE_PLACEHOLDER_SVG,
        srcset: imageSrcSet,
        errorImageSrc: errorImageSrc,
        alt: imageAlt,
        lazyloadOffset: 200,
        lazyload: true,
        ratio: '16:9',
        width: '100%',
        height: '100%',
        fixAspect: true,
        borderRadius: '6px',
        backgroundColor: COLORS.mlbGrey7,
        sizes: imgSizes,
    };

    const videoProps = {
        duration: !disabled && duration && DateUtils.formatTime(duration) || '',
        hideIcon: true,
        live: false,
    };

    const onClickHandler = (event) => {

        if(targetHasDataClickHandler(event)) {

            return;

        }

        onClick(event);

    };

    const Share = (
        <ShareComponent
            mediaId={id}
            labelKey={'share_text'}
            shape={'hollow-borderless'}
            onClick={() => {

                Tracking.trackClipMenuShareButtonClick(slug);

            }}
            iconSize={18}
            size="md"
        />
    );

    const optionsMenuContent = cardContext === cardContexts.REEL_PAGE
        ? (
            <>
                {Share}
                <DeleteReel
                    mediaId={id}
                />
            </>

        )
        : (
            <>
                <ToggleInReel
                    mediaPlayback={mediaPlayback}
                    disabled={!canAddToReel}
                    onClick={
                        isInReel
                            ? Tracking.trackClipMenuRemoveFromReelButtonClick
                            : Tracking.trackClipMenuAddToReelButtonClick
                    }
                />
                {Share}
            </>
        );

    const optionsComponent = (
        <OptionsMenu
            orientation={orientation}
            onPopoverOpen={onPopoverOpen}
            onPopoverClose={onPopoverClose}
            disabled={disabled}
            altText={`${_get(dictionary, 'clip_menu_text', 'Video detail menu')}`}
            optionsMenuContent={optionsMenuContent}
            preventOverflow={preventOverflow}
            popoverPlacement={popoverPlacement}
        />
    );

    return (
        <ContentCard
            url={`${basePath}/${mediaPath}${searchParams}`}
            headline={blurb}
            publishDate={formattedDate}
            imageProps={imageProps}
            orientation={orientation}
            videoProps={videoProps}
            isLoading={isLoading}
            disabled={disabled || isActive || isPopoverOpen}
            handlers={{ onClick: onClickHandler }}
            isActive={isActive}
            ref={ref}
            optionsComponent={(
                <>
                    {isAppView && optionsComponent}
                    {renderStats && renderStats(mediaPlayback)}
                </>

            )}
            isInReel={isInReel}
        />
    );

});

Card.propTypes = {
    appView: PropTypes.string.isRequired,
    basePath: PropTypes.string,
    mediaPlayback: PropTypes.object,
    orientation: PropTypes.string,
    onClick: PropTypes.func,
    isLoading: PropTypes.bool,
    config: PropTypes.object,
    lang: PropTypes.string,
    isActive: PropTypes.bool,
    dictionary: PropTypes.object,
    cardContext: PropTypes.string,
    renderStats: PropTypes.func,
    searchParams: PropTypes.string,
    imgSizes: PropTypes.string,
    preventOverflow: PropTypes.bool,
    popoverPlacement: PropTypes.string,
};

Card.defaultProps = {
    basePath: '',
    mediaPlayback: {},
    orientation: 'portrait',
    onClick: _noop,
    isLoading: false,
    config: {},
    lang: 'en',
    isActive: false,
    dictionary: {},
    cardContext: cardContexts.MEDIA,
    renderStats: null,
    searchParams: '',
    imgSizes: '',
    preventOverflow: true,
    popoverPlacement: 'bottom-end',
};

export default Card;
